<script setup lang="ts">
/**
 * Un geste rendu selon son accès (portage de `boutonGeste`, S:4903-4910) :
 *  • actif : le bouton ;
 *  • inactif : le bouton, puis l'étiquette « Avancé » si c'est le niveau qui
 *    ferme, puis la raison de la matrice en une ligne, écrite sous le bouton ;
 *  • absent : rien.
 *
 * Accessible : un bouton inactif n'est PAS `disabled`. Il porte
 * `aria-disabled="true"`, reste atteignable au clavier et annonce sa raison
 * par `aria-describedby` ; la raison est un texte visible, jamais une seule
 * infobulle. Un clic ou une touche sur lui ne fait rien.
 *
 * Le composant ne juge rien : il reçoit le verdict de `useAcces`.
 */
// ================================
// IMPORTS
// ================================
import { useId } from "vue"
import { ETIQUETTE_AVANCE, type Acces } from "@modules/alarmes-v2/acces/acces"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Le verdict du geste (`useAcces().acces(cle)`). */
        acces: Acces
        /** Le libellé du bouton. */
        libelle: string
        /** Les classes du bouton. */
        classe?: string
        /** Taire l'étiquette « Avancé » (S:4909, `sansEtiquette`). */
        sansEtiquette?: boolean
    }>(),
    {
        classe: "rounded border border-surface-300 px-3 py-1.5 text-sm font-medium text-surface-800",
        sansEtiquette: false,
    }
)

const emit = defineEmits<{
    /** Le geste est actif et la personne l'a demandé. */
    click: [evenement: MouseEvent]
}>()

/** L'identifiant de la raison, lu par `aria-describedby`. */
const idRaison = `geste-raison-${useId()}`

// ================================
// HANDLERS
// ================================
/**
 * Un clic : transmis seulement quand le geste est actif (le serveur refuse
 * de toute façon, l'écran n'envoie pas un geste qu'il sait fermé).
 */
function onClick(evenement: MouseEvent): void {
    if (props.acces.ok) emit("click", evenement)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <button
        v-if="acces.ok"
        type="button"
        :class="classe"
        data-test="geste"
        @click="onClick"
    >
        {{ libelle }}
    </button>
    <span
        v-else-if="acces.affichage === 'inactif'"
        class="inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5"
        data-test="geste-ferme"
    >
        <button
            type="button"
            :class="[classe, 'cursor-not-allowed opacity-60']"
            aria-disabled="true"
            :aria-describedby="idRaison"
            data-test="geste"
            @click.prevent
        >
            {{ libelle }}
        </button>
        <span
            v-if="acces.etiquetteAvance && !sansEtiquette"
            class="inline-flex items-center whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-2 text-xs font-semibold text-primary-700"
            data-test="etiquette-avance"
        >
            {{ ETIQUETTE_AVANCE }}
        </span>
        <span
            :id="idRaison"
            class="basis-full text-xs text-surface-500"
            data-test="raison"
        >
            {{ acces.raison }}
        </span>
    </span>
</template>
